import { useState } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Mail,
  Phone,
  MapPin,
  Send,
  CheckCircle,
  AlertCircle,
  Clock,
  Users,
  Sparkles,
} from "lucide-react";
import Section from "../ui/Section";
import Card from "../ui/Card";
import Button from "../ui/Button";

const contactSchema = z.object({
  name: z.string().min(2, "İsim en az 2 karakter olmalıdır"),
  email: z.string().email("Geçerli bir e-posta adresi giriniz"),
  phone: z.string().min(10, "Telefon numarası en az 10 karakter olmalıdır"),
  company: z.string().min(2, "Kurum adı en az 2 karakter olmalıdır"),
  department: z.enum([
    "radyoloji",
    "kardiyoloji",
    "ic-hastaliklari",
    "yonetim",
    "bilgi-islem",
    "diger",
  ]),
  message: z.string().min(10, "Mesaj en az 10 karakter olmalıdır"),
  demo: z.boolean().default(false),
  newsletter: z.boolean().default(false),
});

type ContactForm = z.infer<typeof contactSchema>;

export default function Contact() {
  const [isSubmitted, setIsSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ContactForm>({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (data: ContactForm) => {
    await new Promise((resolve) => setTimeout(resolve, 2000));
    console.log("Form data:", data);
    setIsSubmitted(true);
    reset();
    setTimeout(() => setIsSubmitted(false), 5000);
  };

  const inputClasses =
    "w-full px-4 py-3 bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500 dark:focus:ring-primary-400/40 dark:focus:border-primary-400 transition-all duration-200 outline-none text-sm";

  return (
    <Section id="contact" background="mesh" padding="xl">
      <div className="text-center mb-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-display-sm md:text-display-md font-display font-bold text-gray-900 dark:text-white mb-5">
            Hemen{" "}
            <span className="text-gradient">Başlayın</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            MultiVision Report ile tıbbi raporlamanızı dönüştürün.
            Uzmanlarımızdan ücretsiz demo alın.
          </p>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* Form */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Card variant="glass" padding="lg">
            <h3 className="text-lg font-display font-bold text-gray-900 dark:text-white mb-5">
              Demo Talep Formu
            </h3>

            {isSubmitted && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-5 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700/40 rounded-xl flex items-center space-x-3"
              >
                <CheckCircle className="text-emerald-600 flex-shrink-0" size={18} />
                <div>
                  <p className="text-emerald-800 dark:text-emerald-300 font-medium text-sm">
                    Talebiniz başarıyla gönderildi!
                  </p>
                  <p className="text-emerald-600 dark:text-emerald-400 text-xs">
                    En kısa sürede size dönüş yapacağız.
                  </p>
                </div>
              </motion.div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                    Ad Soyad *
                  </label>
                  <input
                    {...register("name")}
                    type="text"
                    className={inputClasses}
                    placeholder="Ad Soyad"
                  />
                  {errors.name && (
                    <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle size={12} />
                      {errors.name.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                    E-posta *
                  </label>
                  <input
                    {...register("email")}
                    type="email"
                    className={inputClasses}
                    placeholder="ornek@email.com"
                  />
                  {errors.email && (
                    <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle size={12} />
                      {errors.email.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                    Telefon *
                  </label>
                  <input
                    {...register("phone")}
                    type="tel"
                    className={inputClasses}
                    placeholder="0555 123 45 67"
                  />
                  {errors.phone && (
                    <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle size={12} />
                      {errors.phone.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                    Kurum/Hastane *
                  </label>
                  <input
                    {...register("company")}
                    type="text"
                    className={inputClasses}
                    placeholder="Kurum Adı"
                  />
                  {errors.company && (
                    <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle size={12} />
                      {errors.company.message}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                  Departman *
                </label>
                <select
                  {...register("department")}
                  className={inputClasses}
                >
                  <option value="">Departman Seçiniz</option>
                  <option value="radyoloji">Radyoloji</option>
                  <option value="kardiyoloji">Kardiyoloji</option>
                  <option value="ic-hastaliklari">İç Hastalıkları</option>
                  <option value="yonetim">Yönetim</option>
                  <option value="bilgi-islem">Bilgi İşlem</option>
                  <option value="diger">Diğer</option>
                </select>
                {errors.department && (
                  <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle size={12} />
                    {errors.department.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                  Mesaj *
                </label>
                <textarea
                  {...register("message")}
                  rows={3}
                  className={`${inputClasses} resize-none`}
                  placeholder="İhtiyaçlarınızı ve demo gereksinimlerinizi belirtiniz..."
                />
                {errors.message && (
                  <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle size={12} />
                    {errors.message.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label className="flex items-center space-x-2.5">
                  <input
                    {...register("demo")}
                    type="checkbox"
                    className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                  />
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    Canlı demo seansı talep ediyorum
                  </span>
                </label>
                <label className="flex items-center space-x-2.5">
                  <input
                    {...register("newsletter")}
                    type="checkbox"
                    className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                  />
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    Ürün güncellemelerini e-posta ile almak istiyorum
                  </span>
                </label>
              </div>

              <Button
                type="submit"
                className="w-full"
                loading={isSubmitting}
              >
                <Send size={16} />
                Gönder
              </Button>
            </form>
          </Card>
        </motion.div>

        {/* Contact Info */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="space-y-5"
        >
          {/* Contact Details */}
          <Card variant="glass" padding="lg">
            <h3 className="text-lg font-display font-bold text-gray-900 dark:text-white mb-5">
              İletişim Bilgileri
            </h3>
            <div className="space-y-5">
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-100 to-primary-50 dark:from-primary-900/40 dark:to-primary-800/30 flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-primary-600 dark:text-primary-400" size={18} />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                    Adres
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                    Üniversiteler Mah. 1606. Cd.
                    <br />
                    Cyberpark, Cyberplaza A Blok Z05
                    <br />
                    Bilkent 06800 Çankaya/ANKARA
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-100 to-green-50 dark:from-emerald-900/40 dark:to-green-800/30 flex items-center justify-center flex-shrink-0">
                  <Phone className="text-emerald-600 dark:text-emerald-400" size={18} />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                    Telefon
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">0-312-295-6485</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-100 to-blue-50 dark:from-sky-900/40 dark:to-blue-800/30 flex items-center justify-center flex-shrink-0">
                  <Mail className="text-sky-600 dark:text-sky-400" size={18} />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                    E-posta
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    multivision@yakaza.com.tr
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Why MultiVision */}
          <Card variant="gradient" padding="md">
            <h3 className="text-sm font-display font-bold text-gray-900 dark:text-white mb-4">
              Neden MultiVision?
            </h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-2.5">
                <Clock className="text-primary-600 dark:text-primary-400 flex-shrink-0" size={16} />
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  24 saat içinde demo randevusu
                </span>
              </div>
              <div className="flex items-center space-x-2.5">
                <Users className="text-emerald-600 dark:text-emerald-400 flex-shrink-0" size={16} />
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  Uzman teknik ekip desteği
                </span>
              </div>
              <div className="flex items-center space-x-2.5">
                <CheckCircle className="text-sky-600 dark:text-sky-400 flex-shrink-0" size={16} />
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  KVKK & ISO 27001 uyumlu güvenli sistem
                </span>
              </div>
              <div className="flex items-center space-x-2.5">
                <Sparkles className="text-violet-600 dark:text-violet-400 flex-shrink-0" size={16} />
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  7/7 metrikte hedef karşılanmış performans
                </span>
              </div>
            </div>
          </Card>

          {/* Company Card */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-navy-900 to-primary-950 dark:from-navy-950 dark:to-black p-6 text-white">
            <div className="absolute inset-0 shimmer opacity-50" />
            <div className="absolute -top-4 -right-4 w-20 h-20 bg-primary-500/10 rounded-full" />
            <div className="relative z-10 text-center">
              <h3 className="text-base font-display font-bold text-white mb-1">
                YAKAZA Bilişim Çözümleri
              </h3>
              <p className="text-gray-400 text-xs mb-2">Danışmanlık Ltd. Şti.</p>
              <p className="text-gray-500 text-[10px]">
                Sağlık teknolojilerinde uzman ekibimizle yenilikçi çözümler sunuyoruz.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </Section>
  );
}
